import React, { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Box, Paper, Typography } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import NavigationIcon from "@mui/icons-material/Navigation";
import moment from "moment";
import 'moment-timezone';
import { calcDistanceToPoints } from "Components/Map/utils";
import { getVehicleTrails, vehicleTrailsSearchClipRequests } from "Redux/actions";
import { useDispatch, useSelector } from "react-redux";
import { resetClipRequestDrawer, setClipRequestData } from "../../../../../../../components/Map/components/ClipRequestDrawerMap/redux/slice";
import { setTripsModeActive } from "../../redux/slice";
import { TypeLayer } from '../../../../../../../components/Map/providers/enums'


const METERS_TO_MILES = 1 / 1609.344;

const formatDuration = (ms, messages) => {
  const d = moment.duration(ms);
  const hrs = Math.floor(d.asHours());
  const mins = d.minutes();
  return `${hrs} ${messages["realtimemaps_vehicletrails_trip_hrs"]}, ${mins} ${messages["realtimemaps_vehicletrails_trip_mins"]}`;
};

const formatTime = (isoString, timezone) =>
  isoString
    ? (timezone ? moment(isoString).tz(timezone) : moment(isoString)).format("hh:mm A")
    : "—";

const distanceBetween = (a, b) => {
  if (
    a?.latitude == null ||
    a?.longitude == null ||
    b?.latitude == null ||
    b?.longitude == null
  )
    return 0;
  return calcDistanceToPoints(
    a.latitude,
    a.longitude,
    b.latitude,
    b.longitude
  );
};

const TripCard = React.memo(({ trip, nextTrip, onSelectTrip, isSelected, vehicleTimezone }) => {

  const { user: { isKph } } = useSelector((state) => state.securityRedux);

  const { messages } = useIntl();
  const startTime = trip?.start?.unitTime;
  const endTime = trip?.end?.unitTime;
  const startAddress = trip?.start?.address ?? "—";
  const endAddress = trip?.end?.address ?? "—";
  const driverName = trip?.start?.driverName ?? trip?.end?.driverName ?? "—";

  const durationMs = useMemo(() => {
    if (!startTime || !endTime) return 0;
    return moment(endTime).diff(moment(startTime));
  }, [startTime, endTime]);

  const inactivityMs = useMemo(() => {
    if (!nextTrip?.start?.unitTime || !endTime) return null;
    return moment(nextTrip.start.unitTime).diff(moment(endTime));
  }, [endTime, nextTrip?.start?.unitTime]);
  const inactivityFormatted =
    inactivityMs != null ? formatDuration(inactivityMs, messages) : null;

  const distanceMeters = useMemo(
    () => distanceBetween(trip?.start, trip?.end),
    [trip?.start, trip?.end]
  );
  const distanceMiles = isKph ? parseFloat(distanceMeters * 0.001).toFixed(2) : parseFloat(distanceMeters * 0.000621371).toFixed(2);
  const durationFormatted = formatDuration(durationMs, messages);

  const iconStyle = { color: "rgba(0, 0, 0, 0.54)", fontSize: 20 };
  const timelineDotSx = {
    width: 10,
    height: 10,
    borderRadius: "50%",
    bgcolor: "primary.main",
    flexShrink: 0,
  };
  const timelineLineSx = {
    width: 2,
    bgcolor: "primary.main",
    flexShrink: 0,
    alignSelf: "stretch",
    minHeight: 8,
  };

  return (
    <Paper
      data-testid="vehicle-trip-card-container"
      onClick={() => onSelectTrip(trip)}
      elevation={0}
      sx={{
        p: 2,
        border: "1px solid",
        borderColor: isSelected ? "primary.main" : "divider",
        borderRadius: 1,
        bgcolor: isSelected ? "#82c7ff82" : "background.paper",
        cursor: "pointer",
        boxShadow: isSelected
          ? "0 4px 12px rgba(25, 118, 210, 0.25)"
          : "none",
        "&:hover": {
          backgroundColor: isSelected
            ? "#82c7ff82"
            : "rgba(0, 0, 0, 0.05)",
        },
        mt: '4px'
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
        <PersonIcon sx={{ ...iconStyle, mr: 1 }} />
        <Typography variant="body1" fontWeight={500}>
          {driverName}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "stretch" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mr: 1.5,
          }}
        >
          <Box sx={timelineDotSx} />
          <Box sx={{ ...timelineLineSx, flex: 1, minHeight: 24 }} />
          <Box sx={timelineDotSx} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "space-between", py: 0.25 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              {formatTime(startTime, vehicleTimezone)}
            </Typography>
            <Typography variant="body2" noWrap title={startAddress}>
              {startAddress}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              py: 0.5,
            }}
          >
            <NavigationIcon sx={{ color: "success.main", fontSize: 18 }} />
            <Typography variant="body2" color="text.secondary">
              {durationFormatted} • {distanceMiles}{" "}
              {messages[isKph ? "kilometers" : "miles"]}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              {formatTime(endTime, vehicleTimezone)}
            </Typography>
            <Typography variant="body2" noWrap title={endAddress}>
              {endAddress}
            </Typography>
          </Box>
        </Box>
      </Box>

      {inactivityFormatted != null && (
        <Box
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
            mt: 1.5,
            pt: 1.5,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {inactivityFormatted}
          </Typography>
        </Box>
      )}
    </Paper>
  );
});

export const VehicleTripsList = ({ vehicleTrips, mapRef, vehicleTimezone }) => {
  const { messages } = useIntl();
  const dispatch = useDispatch();
  const [selectedTripStartTime, setSelectedTripStartTime] = useState(null);

  const sortedTrips = useMemo(() => {
    const items = vehicleTrips?.data ?? [];
    const valid = items.filter(
      (t) => t?.start?.unitTime && t?.end?.unitTime
    );
    return [...valid].sort(
      (a, b) =>
        new Date(a.start.unitTime).getTime() -
        new Date(b.start.unitTime).getTime()
    );
  }, [vehicleTrips]);

  if (!sortedTrips.length) return null;

  const handleSelectTrip = useCallback((trip) => {
    const tripStartTime = trip?.start?.unitTime ?? null;

    if (selectedTripStartTime === tripStartTime) {
      setSelectedTripStartTime(null);
      dispatch(setTripsModeActive(false));
      mapRef?.current?.hideMarkerModal();
      mapRef?.current?.removeLayer(TypeLayer.VehicleTrails);
      dispatch(resetClipRequestDrawer());
      dispatch(setClipRequestData());
      return;
    }

    setSelectedTripStartTime(tripStartTime);
    dispatch(setTripsModeActive(true));
    mapRef?.current?.hideMarkerModal();
    mapRef?.current?.removeLayer(TypeLayer.VehicleTrails);
    const startDateUtc = moment.utc(trip?.start?.unitTime);
    const endDateUtc = moment.utc(trip?.end?.unitTime);

    const paramToGetTrails = {
      unitId: trip?.start?.deviceId,
      startDate: startDateUtc.format(),
      endDate: endDateUtc.format(),
      isATrip: true,
      limit: 300,
      offset: 0,
      useEcuSpeed: true,
      unitType: 'Vehicle',
      kph: true
    }
    dispatch(resetClipRequestDrawer());
    dispatch(setClipRequestData());
    dispatch(getVehicleTrails(paramToGetTrails));

    // Load clip requests for vehicle trails
    dispatch(
      vehicleTrailsSearchClipRequests({
        limit: 50,
        dateRange: {
          start: startDateUtc.format(),
          end: endDateUtc.format(),
        },
        resetSearch: true,
        offset: 0,
        entities: [{ name: "All", id: -1 }],
        hasStarred: false,
        hasNotes: false,
        isDeleted: false,
        status: "all",
        openSearch: "",
      })
    );
  }, [selectedTripStartTime, dispatch, mapRef]);

  return (
    <Box 
      data-testid="vehicle-trips-list"
      sx={{
        mt: 1,
        maxHeight: "38vh",
        overflowY: "auto",
      }}
    >
      {sortedTrips.map((trip, index) => (
        <TripCard
          key={`trip-${trip?.start?.unitTime}-${index}`}
          trip={trip}
          nextTrip={sortedTrips[index + 1] ?? null}
          messages={messages}
          onSelectTrip={handleSelectTrip}
          isSelected={selectedTripStartTime === trip?.start?.unitTime}
          vehicleTimezone={vehicleTimezone}
        />
      ))}
    </Box>
  );
};
